import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import CampaignCard from "../components/CampaignCard";
import { Search, SlidersHorizontal, Gamepad2 } from "lucide-react";

function CampaignsPage() {
  const { userProfile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");

  useEffect(() => {
    async function fetchActiveCampaigns() {
      try {
        const q = query(
          collection(db, "campaigns"),
          where("status", "==", "active")
        );
        const snap = await getDocs(q);
        setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Failed to fetch campaigns:", err);
      }
      setLoading(false);
    }
    fetchActiveCampaigns();
  }, []);

  const filtered = campaigns.filter((c) => {
    const matchesSearch =
      !searchTerm ||
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.steamAppId?.includes(searchTerm);
    const matchesPlatform =
      !platformFilter ||
      (c.platforms && c.platforms.includes(platformFilter));
    return matchesSearch && matchesPlatform;
  });

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-1">Active Campaigns</h1>
          <p className="text-gray-400">
            {userProfile?.role === "influencer"
              ? "Browse campaigns and find games to promote."
              : "All currently active campaigns on the platform."}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title, description, or Steam App ID..."
              className="w-full bg-gray-900/50 border border-gray-800 rounded-xl py-2.5 pl-11 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm"
            />
          </div>
          <div className="relative">
            <SlidersHorizontal className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="bg-gray-900/50 border border-gray-800 rounded-xl py-2.5 pl-10 pr-8 text-white appearance-none focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all text-sm cursor-pointer"
            >
              <option value="">All Platforms</option>
              <option value="youtube">YouTube</option>
              <option value="twitch">Twitch</option>
              <option value="tiktok">TikTok</option>
              <option value="twitter">X / Twitter</option>
              <option value="instagram">Instagram</option>
              <option value="kick">Kick</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-sm text-gray-500 mb-4">
            {filtered.length} campaign{filtered.length !== 1 ? "s" : ""} found
          </p>
        )}

        {/* Campaign List */}
        {loading ? (
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
            <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-12 text-center">
            <Gamepad2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-gray-300">
              {searchTerm || platformFilter
                ? "No campaigns match your filters"
                : "No active campaigns yet"}
            </h3>
            <p className="text-gray-500">
              {searchTerm || platformFilter
                ? "Try adjusting your search or filters."
                : "Check back soon for new campaigns."}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} showSteamAppId={false} linkTo={`/campaigns/${campaign.id}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignsPage;
